import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { 
  BrokenImage as BrokenImageIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { PatternPreviewProps, PATTERN_CONSTANTS } from '../../types/pattern';

const PatternPreview: React.FC<PatternPreviewProps> = ({
  pattern,
  view,
  size = 'medium',
  showFallback = true,
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const dimensions = PATTERN_CONSTANTS.PREVIEW_SIZES[size];
  const svgContentField = view === 'front' ? pattern.front_svg_content : pattern.side_svg_content;

  // Load SVG content
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      // Use embedded SVG content from database
      if (svgContentField) {
        console.log(`Using embedded ${view} SVG content for pattern ${pattern.id}`);
        setSvgContent(svgContentField);
      } else {
        setSvgContent(null);
      }
    } catch (err) {
      console.error('Error loading SVG:', err);
      setError(err instanceof Error ? err.message : 'Failed to load SVG');
      setSvgContent(null);
    } finally {
      setLoading(false);
    }
  }, [svgContentField, retryCount, view, pattern.id]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const generateFallbackSVG = () => {
    const { width, height } = dimensions;
    return `
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f5f5f5" stroke="#ddd" stroke-width="2" rx="8"/>
        <text x="50%" y="40%" text-anchor="middle" fill="#999" font-size="12" font-family="Arial, sans-serif">
          ${pattern.description}
        </text>
        <text x="50%" y="55%" text-anchor="middle" fill="#666" font-size="10" font-family="Arial, sans-serif">
          ${view.toUpperCase()} VIEW
        </text>
        <text x="50%" y="70%" text-anchor="middle" fill="#999" font-size="8" font-family="Arial, sans-serif">
          No SVG Available
        </text>
      </svg>
    `;
  };

  const cleanSvgContent = (content: string) => {
    try {
      // Remove any XML declaration
      let cleaned = content.replace(/<\?xml[^>]*\?>/g, '');

      // Remove DOCTYPE declarations
      cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/g, '');

      // Trim whitespace
      cleaned = cleaned.trim();

      // Check if it's a complete SVG or just SVG fragments
      if (!cleaned.startsWith('<svg')) {
        // If it doesn't start with <svg>, wrap it in an SVG container
        // Using a larger viewBox to accommodate jersey shapes and transforms
        cleaned = `<svg width="${dimensions.width}" height="${dimensions.height}" viewBox="-200 -100 800 600" xmlns="http://www.w3.org/2000/svg" style="max-width: 100%; max-height: 100%; display: block; background: white;">
          ${cleaned}
        </svg>`;
      } else {
        // Add dimensions if missing
        if (!cleaned.includes('width=') || !cleaned.includes('height=')) {
          cleaned = cleaned.replace(
            '<svg',
            `<svg width="${dimensions.width}" height="${dimensions.height}"`
          );
        }

        // Add viewBox if missing
        if (!cleaned.includes('viewBox=')) {
          cleaned = cleaned.replace(
            '<svg',
            `<svg viewBox="0 0 ${dimensions.width} ${dimensions.height}"`
          );
        }

        // Ensure SVG has proper styling for visibility
        if (!cleaned.includes('style=')) {
          cleaned = cleaned.replace(
            '<svg',
            '<svg style="max-width: 100%; max-height: 100%; display: block; background: white;"'
          );
        }
      }

      return cleaned;
    } catch (err) {
      console.error('Error cleaning SVG content:', err);
      return content;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: dimensions.width,
            height: dimensions.height,
            bgcolor: 'grey.100',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error && showFallback) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: dimensions.width,
            height: dimensions.height,
            bgcolor: 'error.light',
            borderRadius: 1,
            p: 1,
          }}
        >
          <BrokenImageIcon color="error" />
          <Typography variant="caption" color="error" textAlign="center" sx={{ mt: 1 }}>
            Failed to load
          </Typography>
          <Tooltip title="Retry loading">
            <IconButton size="small" onClick={handleRetry} color="error">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (!svgContent) {
      if (showFallback) {
        return (
          <div
            dangerouslySetInnerHTML={{ __html: generateFallbackSVG() }}
            style={{
              width: dimensions.width,
              height: dimensions.height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '4px',
              // Scale up side content since it's 5 times smaller
              ...(view === 'side' && {
                transform: 'scale(5)',
                transformOrigin: 'center',
              }),
            }}
          />
        );
      } else {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: dimensions.width,
              height: dimensions.height,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'grey.300',
            }}
          >
            <ImageIcon color="disabled" />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              No {view} SVG
            </Typography>
          </Box>
        );
      }
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: cleanSvgContent(svgContent) }}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '4px',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
          // Scale up side content since it's 5 times smaller
          ...(view === 'side' && {
            transform: 'scale(5)',
            transformOrigin: 'center',
          }),
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: dimensions.height,
        backgroundColor: '#f8f9fa',
        backgroundImage: `
          radial-gradient(circle at 1px 1px, rgba(0,0,0,0.1) 1px, transparent 0)
        `,
        backgroundSize: '20px 20px',
        borderRadius: 1,
        padding: 1,
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default PatternPreview;
